// The three vocabularies around a SubscriptionOffer, kept apart on purpose: the DB enum a row is
// filed under, the feature tokens the server's entitlement check recognises, and product's module -> token-set display
// mapping (which includes package rows like "Expert Sourcing - Pro" that are not modules).

// SubscriptionOffers.module ENUM values, in column order. Append only — a new value at the end keeps
// the MODIFY COLUMN instant.
const SUBSCRIPTION_OFFER_MODULES = [
  'core',
  'gifting',
  'sociallistening',
  'analytics',
  'campaigns',
  'communications',
  'events',
  'opportunities',
  'spotlights'
];

const BRAND_UNLOCK_FEATURES = [
  // Affiliates
  'AFFILIATES', // $99 per month, used for small subset of brands just setting up affiliate
  'PROMOTIONS',
  'CORE', // $999 per month
  // Analytics
  'ANALYTICS',
  'CUSTOM_RATES',
  'CUSTOM_CODES',
  'CHAT_WITH_PROMOTERS',
  'LISTS',
  // Gifting
  'LOOKBOOKS',
  'GIFTING',
  'GIFTING_LITE',
  'UNLIMITED_GIFTING',
  'PROMOTER_GIFTING',
  'DISCOVER',
  'DISCOVER_2.0',
  'LOOKALIKES',
  // Gifting - Add Ons
  'LOOKBOOK_SHOPMY_ORDER_HANDLING',
  // Analytics
  'CROSS_RETAILER_ANALYTICS',
  'ADVANCED_TALENT_ANALYTICS',
  'ADVANCED_TALENT_ANALYTICS_NON_PROMOTERS',
  'COMPETITOR_BRANDS',
  // Collaborations
  'COLLABORATE',
  // Social Mentions
  'SOCIAL_MENTIONS_FEED',
  // Spotlights
  'SPOTLIGHTS',
  // Communications (CHAT_MESSAGES_500_PER_MONTH grants chat with a monthly send allowance, not unlimited)
  'UNLIMITED_CHAT',
  'BULK_MESSAGING',
  'CHAT_MESSAGES_500_PER_MONTH',
  // Opportunities
  'OPPORTUNITIES',
  // Team Management — seat totals, not increments. An offer granting none leaves the brand on the default.
  'TEAM_MANAGEMENT_SEATS_1',
  'TEAM_MANAGEMENT_SEATS_6',
  'TEAM_MANAGEMENT_SEATS_10',
  'TEAM_MANAGEMENT_UNLIMITED_SEATS'
];

/*
  The official module → enum-set mapping (source: the "Module Enums" sheet owned by product). Order
  matters: chips render in this order, and ties between equal-size sets resolve to the earlier row.
  Omitted sheet rows: "Scale - Track" (a package, not a module; it would self-match as one chip) and
  "Lookbooks" / "Sourcing Support" / "Opportunities Management" (exact duplicates of Gifting /
  Expert Sourcing / Opportunities Planning).
*/
const MODULE_ENUM_SETS = [
  { title: 'Core', enums: ['AFFILIATES', 'ANALYTICS', 'CUSTOM_RATES', 'CUSTOM_CODES', 'CHAT_WITH_PROMOTERS', 'LISTS', 'CORE', 'TALENT_TAB'] },
  { title: '360 Management', enums: ['360_MANAGEMENT'] },
  { title: 'Additional Domains', enums: ['ADDITIONAL_DOMAINS'] },
  { title: 'Affiliates', enums: ['AFFILIATES', 'TALENT_TAB', 'CUSTOM_RATES', 'CUSTOM_CODES'] },
  { title: 'Discover', enums: ['DISCOVER', 'DISCOVER_2.0'] },
  { title: 'Gifting', enums: ['GIFTING', 'LOOKBOOKS', 'DISCOVER', 'DISCOVER_2.0', 'LOOKALIKES'] },
  { title: 'Expert Sourcing', enums: ['EXPERT_SOURCING'] },
  { title: 'Gifting Management', enums: ['GIFTING_MANAGEMENT'] },
  { title: 'Placing Non-Shopify Orders', enums: ['LOOKBOOK_SHOPMY_ORDER_HANDLING'] },
  { title: 'Unlimited Gifting', enums: ['UNLIMITED_GIFTING', 'GIFTING', 'LOOKBOOKS', 'DISCOVER', 'DISCOVER_2.0', 'LOOKALIKES'] },
  { title: 'Expert Sourcing - Lite', enums: ['EXPERT_SOURCING', 'EXPERT_SOURCING_LITE'] },
  { title: 'Expert Sourcing - Pro', enums: ['EXPERT_SOURCING', 'EXPERT_SOURCING_UNLIMITED'] },
  { title: 'Social Listening', enums: ['SOCIAL_MENTIONS_FEED'] },
  {
    title: 'Advanced Analytics',
    enums: ['CROSS_RETAILER_ANALYTICS', 'ADVANCED_TALENT_ANALYTICS', 'ADVANCED_TALENT_ANALYTICS_NON_PROMOTERS', 'COMPETITOR_BRANDS']
  },
  { title: 'Collaborations', enums: ['COLLABORATE'] },
  { title: 'Campaign Management', enums: ['CAMPAIGN_MANAGEMENT'] },
  { title: 'Campaign Management - Medium', enums: ['CAMPAIGN_MANAGEMENT', 'CAMPAIGN_MANAGEMENT_MEDIUM'] },
  { title: 'Campaign Management - Large', enums: ['CAMPAIGN_MANAGEMENT', 'CAMPAIGN_MANAGEMENT_LARGE'] },
  { title: 'Communications', enums: ['UNLIMITED_CHAT', 'BULK_MESSAGING'] },
  { title: 'Communications', enums: ['CHAT_MESSAGES_500_PER_MONTH', 'BULK_MESSAGING'] },
  { title: 'Communications', enums: ['CHAT_MESSAGES_500_PER_MONTH'] },
  { title: 'Opportunities', enums: ['OPPORTUNITIES', 'DISCOVER', 'DISCOVER_2.0', 'LOOKALIKES'] },
  { title: 'Opportunities Planning', enums: ['OPPORTUNITIES', 'DISCOVER', 'DISCOVER_2.0', 'LOOKALIKES', 'OPPORTUNITIES_PLANNING'] },
  { title: 'Spotlights', enums: ['SPOTLIGHTS'] },
  { title: 'Promoter Chat', enums: ['CHAT_WITH_PROMOTERS'] },
  { title: 'Promoter Gifting', enums: ['PROMOTER_GIFTING', 'LOOKBOOKS'] },
  { title: 'Single Team Seat', enums: ['TEAM_MANAGEMENT_SEATS_1'] },
  { title: 'Additional Team Seats', enums: ['TEAM_MANAGEMENT_SEATS_6'] },
  { title: 'Additional Team Seats', enums: ['TEAM_MANAGEMENT_SEATS_10'] },
  { title: 'Unlimited Team Seats', enums: ['TEAM_MANAGEMENT_UNLIMITED_SEATS'] }
];

const offerUnlocksFeature = (offer, feature) =>
  [offer?.canAccessEnum, offer?.canAccessDeprecatedEnum].some(value => value?.split(',').some(token => token.trim() === feature));

const isComplimentaryTrialItem = item => !!item?.doNotConvertTrialToPaid && !item.stripeSubscriptionItemId;

// Converting items retain access until cancellation/Stripe sync; only non-converting, non-Stripe trials expire by clock.
const isSubscriptionItemActive = (item, now = Date.now()) =>
  !!item && !item.isCancelled && (!isComplimentaryTrialItem(item) || new Date(item.trialEndsAt).getTime() > now);

const isPromotionsAddOn = offer => !offer?.segment && offerUnlocksFeature(offer, 'PROMOTIONS');

const pickPackageOffer = offers => {
  const packages = offers.filter(offer => offer.segment);
  let candidates = packages.filter(offer => !offer.isCancelled);
  // Cancellation also cancels the items: retain the last package's segment, not its paid access.
  if (!candidates.length && packages.length) {
    const latestCancellation = Math.max(...packages.map(offer => new Date(offer.cancelledAt || 0).getTime()));
    candidates = packages.filter(offer => new Date(offer.cancelledAt || 0).getTime() === latestCancellation);
    const launch = candidates.find(offer => String(offer.segment).trim().toLowerCase() === 'launch');
    if (launch) return launch;
  }
  return candidates.sort((a, b) => a.id - b.id)[0] || null;
};

const isPromotionsPackageEligible = offers => String(pickPackageOffer(offers)?.segment || '').trim().toLowerCase() !== 'launch';

module.exports = {
  offerUnlocksFeature,
  isComplimentaryTrialItem,
  isSubscriptionItemActive,
  isPromotionsAddOn,
  isPromotionsPackageEligible,
  SUBSCRIPTION_OFFER_MODULES,
  BRAND_UNLOCK_FEATURES,
  MODULE_ENUM_SETS
};
